import {
  type FeatureFlag,
  type FeatureFlagChange,
  type FlagDefinition as BackendFlagDefinition,
  FlagState,
  type User,
} from '@kalos/kalos-rpc';
import { addDays, endOfDay, format, isAfter, isValid, parseISO } from 'date-fns';

import { cleanupDays, type FlagDefinition } from '../../featureFlags/registry';
import { featureFlagAdminCheck } from '../../tools/permissionChecks';

// Limits the backend enforces (kalos-core v2/server/feature_flag); checked here
// too so the dialogs can say what is wrong before a round trip.
export const MAX_KEEP_DAYS = 90;
export const MAX_KEEP_REASON_LENGTH = 255;
export const MAX_OWNERS = 20;
/** The sweep purges a retired row this many days after retiring it. */
export const PURGE_AFTER_DAYS = 30;
/** changed_by / updated_by for rows the daily sweep writes. */
export const SWEEP_USER_ID = 0;

/** One flag as the admin page lists it: registry definitions merged with its row. */
export interface FlagEntry {
  key: string;
  /** From the dashboard registry, else the backend's. Empty when no registry holds the key. */
  description: string;
  /** Dashboard tickets first, then any only the backend lists. */
  tickets: string[];
  inDashboard: boolean;
  inBackend: boolean;
  /** The larger window when both registries hold the key, as the sweep reads it. */
  cleanupAfterDays: number;
  /** Absent until the flag is first switched, which reads as off. */
  row?: FeatureFlag;
}

/**
 * Merges ListFlags' rows with both registries, by key.
 *
 * `live` is every key a registry holds, plus any unretired row whose key no
 * registry holds any more (the next sweep retires it). `retired` is every
 * retired row whose key is in neither registry; a retired row whose key has
 * come back stays in `live`, where its state shows and switching is refused.
 */
export function mergeFlags(
  rows: readonly FeatureFlag[],
  dashboard: Readonly<Record<string, FlagDefinition>>,
  backend: readonly BackendFlagDefinition[],
): { live: FlagEntry[]; retired: FeatureFlag[] } {
  const rowsByKey = new Map(rows.map((r) => [r.key, r]));
  const backendByKey = new Map(backend.map((d) => [d.key, d]));
  const registryKeys = new Set([...Object.keys(dashboard), ...backendByKey.keys()]);
  const liveKeys = new Set([
    ...registryKeys,
    ...rows.filter((r) => !r.retiredAt).map((r) => r.key),
  ]);

  const live = [...liveKeys].sort().map((key): FlagEntry => {
    const fe = Object.hasOwn(dashboard, key) ? dashboard[key] : undefined;
    const be = backendByKey.get(key);
    return {
      key,
      description: fe?.description || be?.description || '',
      tickets: [...new Set([...(fe?.tickets ?? []), ...(be?.tickets ?? [])])],
      inDashboard: !!fe,
      inBackend: !!be,
      cleanupAfterDays: Math.max(fe ? cleanupDays(fe) : 0, be ? cleanupDays(be) : 0),
      row: rowsByKey.get(key),
    };
  });
  const retired = rows
    .filter((r) => r.retiredAt && !registryKeys.has(r.key))
    .sort((a, b) => a.key.localeCompare(b.key));
  return { live, retired };
}

export const isOn = (entry: Pick<FlagEntry, 'row'>) => entry.row?.state === FlagState.ON;

/**
 * When the sweep will call the flag due: the later of enabled_at plus the
 * cleanup window and the end of the keep_until day. Undefined while the flag
 * is off or retired, when no clock is running.
 */
export function cleanupDueAt(entry: FlagEntry): Date | undefined {
  const row = entry.row;
  if (!row || row.state !== FlagState.ON || !row.enabledAt || row.retiredAt) return undefined;
  const window = addDays(parseISO(row.enabledAt), entry.cleanupAfterDays);
  if (!row.keepUntil) return window;
  const kept = endOfDay(parseISO(row.keepUntil));
  return isAfter(kept, window) ? kept : window;
}

/** The day the sweep deletes a retired row. */
export const purgeDate = (row: Pick<FeatureFlag, 'retiredAt'>) =>
  addDays(parseISO(row.retiredAt), PURGE_AFTER_DAYS);

/** A wire date (RFC 3339 or YYYY-MM-DD) for display; empty stays empty. */
export const formatDay = (value: string | Date | undefined) => {
  if (!value) return '';
  const date = typeof value === 'string' ? parseISO(value) : value;
  return isValid(date) ? format(date, 'MMM d, yyyy') : String(value);
};

export const formatMoment = (value: string) => {
  if (!value) return '';
  const date = parseISO(value);
  return isValid(date) ? format(date, 'MMM d, yyyy h:mm a') : value;
};

export const ownsFlag = (row: FeatureFlag | undefined, userId: number) =>
  !!userId && !!row?.ownerUserIds.includes(userId);

/**
 * Who may open the admin page: FeatureFlagAdmin, or the owner of a flag that
 * is still switchable. It only hides the page; every write is checked again
 * by the backend.
 */
export const canOpenFeatureFlags = (user: User, rows: ReadonlyMap<string, FeatureFlag>) =>
  featureFlagAdminCheck(user) ||
  [...rows.values()].some((row) => !row.retiredAt && ownsFlag(row, user.id));

/** What is wrong with a keep request, or undefined. Mirrors KeepFlag's checks. */
export function keepError(keepUntil: string, reason: string, today: Date): string | undefined {
  const until = parseISO(keepUntil);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(keepUntil) || !isValid(until)) return 'Pick a date.';
  if (keepUntil <= format(today, 'yyyy-MM-dd')) return 'Pick a date after today.';
  if (keepUntil > format(addDays(today, MAX_KEEP_DAYS), 'yyyy-MM-dd')) {
    return `Pick a date at most ${MAX_KEEP_DAYS} days out.`;
  }
  const trimmed = reason.trim();
  if (!trimmed) return 'Say why the flag needs to stay.';
  if ([...trimmed].length > MAX_KEEP_REASON_LENGTH) {
    return `Keep the reason to ${MAX_KEEP_REASON_LENGTH} characters.`;
  }
  return undefined;
}

/**
 * One change-log row in words. from_state/to_state mean something only for
 * `set`; every other action is read from `action` and `detail`.
 */
export function describeChange(
  change: Pick<FeatureFlagChange, 'action' | 'toState' | 'detail'>,
  nameOf: (userId: number) => string,
): string {
  switch (change.action) {
    case 'set':
      return change.toState === FlagState.ON ? 'Turned on' : 'Turned off';
    case 'keep':
      return `Kept ${change.detail}`;
    case 'owners': {
      const ids = change.detail
        .split(',')
        .map((id) => Number(id.trim()))
        .filter((id) => id > 0);
      return ids.length ? `Owners set to ${ids.map(nameOf).join(', ')}` : 'Owners removed';
    }
    case 'retire':
      return `Retired: ${change.detail || 'in no deployed registry'}`;
    case 'purge':
      return `Purged: ${change.detail}`;
    case 'issue':
      return `Cleanup issue ${change.detail}`;
    default:
      return [change.action, change.detail].filter(Boolean).join(': ');
  }
}
